import { ClientConfig } from '../../types/profile';

/**
 * Builds an RFC 6350 compliant vCard (v3.0) with full UTF-8 support for Arabic & English names and fields.
 */
export function buildVCardString(config: ClientConfig, locale = 'ar'): string {
  const identity = config.identity;
  const contacts = config.contacts;

  const displayName = identity.name[locale] || identity.name.en || identity.name.ar || config.slug;
  const organization = identity.name[locale] || identity.name.en || 'JetOwn';
  const title = identity.tagline[locale] || identity.tagline.en || '';
  const note = identity.description[locale] || identity.description.en || '';
  const address = contacts.address ? (contacts.address[locale] || contacts.address.en || '') : '';

  const lines: string[] = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN;CHARSET=UTF-8:${displayName}`,
    `N;CHARSET=UTF-8:${displayName};;;;`,
    `ORG;CHARSET=UTF-8:${organization}`,
    `TITLE;CHARSET=UTF-8:${title}`,
  ];

  if (contacts.phone) {
    lines.push(`TEL;TYPE=WORK,VOICE:${contacts.phone}`);
  }

  if (contacts.whatsapp && contacts.whatsapp !== contacts.phone) {
    lines.push(`TEL;TYPE=CELL,VOICE:${contacts.whatsapp}`);
  }

  if (contacts.email) {
    lines.push(`EMAIL;TYPE=PREF,INTERNET:${contacts.email}`);
  }

  if (contacts.website) {
    lines.push(`URL:${contacts.website}`);
  }

  if (config.profileUrl) {
    lines.push(`X-PROFILE-URL:${config.profileUrl}`);
  }

  if (address) {
    lines.push(`ADR;TYPE=WORK;CHARSET=UTF-8:;;;${address};;;`);
  }

  if (note) {
    // Escape newlines in notes
    const escapedNote = note.replace(/\n/g, '\\n');
    lines.push(`NOTE;CHARSET=UTF-8:${escapedNote}`);
  }

  lines.push('END:VCARD');

  return lines.join('\r\n');
}

/**
 * Triggers a browser download of the .vcf card.
 */
export function downloadVCard(config: ClientConfig, locale = 'ar'): void {
  const vCardData = buildVCardString(config, locale);
  const blob = new Blob([vCardData], { type: 'text/vcard;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const safeFilename = `${config.slug || 'contact'}-profile.vcf`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', safeFilename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

